import ReflectField from "@/components/ReflectField";
import StrongsAccordion from "@/components/StrongsAccordion";
import CrossReferences from "@/components/CrossReferences";
import CopyButton from "@/components/CopyButton";
import ScriptureHero from "@/components/ScriptureHero";
import MarkdownLite from "@/components/MarkdownLite";
import TranslationPicker from "@/components/TranslationPicker";
import ReadingLevelToggle from "@/components/ReadingLevelToggle";
import TeachAndShare from "@/components/TeachAndShare";
import InlineStrongs from "@/components/InlineStrongs";
import { ListenButton, ListenIconButton } from "@/components/ListenButton";
import StudyActions from "@/components/StudyActions";
import AudioSettings from "@/components/AudioSettings";
import ReadAlongPanel from "@/components/ReadAlongPanel";
import { stop as stopAudio, prefetch as prefetchAudio } from "@/lib/audioReader";
import { buildOverviewText, buildSectionText, buildSummaryText, buildExerciseText } from "@/lib/studyText";
import SharePublicDialog from "@/components/SharePublicDialog";
import HighlightableText from "@/components/HighlightableText";
import { AlignLeft, Share2, Highlighter, SlidersHorizontal, ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import { api } from "@/lib/kfmApi";
import { HeartHandshake, ScrollText } from "lucide-react";
import { useDeeperNav } from "@/context/deeperNav";
import { toast } from "sonner";

function Paragraphs({ text, className, sessionId, blockPrefix, highlights, onHighlightsChange, sharePath }) {
  const parts = (text || "").split(/\n{2,}/).filter((p) => p.trim());
  return (
    <>
      {parts.map((p, i) => (
        <p key={i} className={className}>
          <HighlightableText
            sessionId={sessionId}
            block={`${blockPrefix}-${i}`}
            text={p.trim()}
            highlights={highlights}
            onChange={onHighlightsChange}
            renderText={(t) => <InlineStrongs text={t} sessionId={sessionId} />}
            sharePath={sharePath}
            cardKind="companion"
          />
        </p>
      ))}
    </>
  );
}

export default function StructuredStudy({ session, hideStrongs = false, onPassageChange, onStudyReload, showGenerators = false }) {
  const study = session.study || {};
  const passage = session.passage || {};
  const verses = passage.verses || [];
  const { requestOpen: openGoDeeper } = useDeeperNav();
  const answers = session.answers || {};

  const [readAlong, setReadAlong] = useState(false);
  const [teachEnabled, setTeachEnabled] = useState(false);
  const [highlights, setHighlights] = useState(session.highlights || []);
  const [toolsOpen, setToolsOpen] = useState(false);
  const studySharePath = `/study/sessions/${session.id}/share`;

  // Nudge people to try highlighting once, via an unobtrusive toast, instead of a
  // persistent banner sitting in the way of every study.
  useEffect(() => {
    if (localStorage.getItem("ow_hl_hint_seen")) return;
    localStorage.setItem("ow_hl_hint_seen", "1");
    toast(
      <span className="flex items-center gap-2">
        <Highlighter className="w-4 h-4 shrink-0 text-[#00E5FF]" strokeWidth={1.75} />
        Select any word or phrase in this study to highlight it and pin a note in the margin.
      </span>,
      { className: "border border-[#00E5FF]/30 bg-[#10222A] text-[#00E5FF]", duration: 6000 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/me/subscription")
      .then(({ data }) => {
        setTeachEnabled(!!(data.features?.teach_share?.enabled));
        // Pre-load the study narration in the background (if the owner has it enabled)
        // so tapping Listen is instant. Charged once per study, then cached forever.
        if (data.tts_prewarm) {
          try { prefetchAudio(fullStudyText(), session.id); } catch (e) { /* ignore */ }
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Normalize text before it's spoken: drop verse numbers from scripture (so the
  // narrator doesn't read "1Blessed…") and expand inline refs like "v3" → "verse 3".
  const cleanForSpeech = (s) =>
    (s || "")
      .replace(/\bvv\.?\s*(\d+)\s*[-–—]\s*(\d+)/gi, "verses $1 to $2")
      .replace(/\bv\.?\s*(\d+)\b/gi, "verse $1");

  const scriptureText = verses.map((v) => (v.text || "").trim()).filter(Boolean).join(" ");
  const fallbackTranslation =
    passage.requested_translation && passage.requested_translation !== passage.translation;

  // Stop any narration when leaving this study.
  useEffect(() => () => stopAudio(), []);

  const sectionText = (sec) => {
    const parts = [];
    if (sec.heading) parts.push(sec.heading + ".");
    if (sec.commentary) parts.push(sec.commentary);
    (sec.reflect || []).forEach((q) => parts.push("Reflect. " + q));
    return cleanForSpeech(parts.join("\n\n"));
  };

  const fullStudyText = () => {
    const parts = [];
    if (study.title) parts.push(study.title + ".");
    if (study.overview) parts.push(study.overview);
    if (scriptureText) parts.push(study.passage_reference + ". " + scriptureText);
    (study.sections || []).forEach((sec) => parts.push(sectionText(sec)));
    if (study.summary) parts.push("Summary. " + study.summary);
    if (study.exercise) parts.push("An exercise for you. " + study.exercise);
    return cleanForSpeech(parts.filter(Boolean).join("\n\n"));
  };

  return (
    <div className="space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header */}
      <header className="space-y-3">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-500">
          <span className="text-[#00E5FF]">{study.passage_reference}</span>
          <span>·</span>
          <TranslationPicker session={session} onPassageChange={onPassageChange} />
          <span>·</span>
          <span className="capitalize">{session.depth}</span>
        </div>
        <h1 className="text-3xl text-white leading-tight">
          <HighlightableText
            sessionId={session.id}
            block="study-title"
            text={study.title || ""}
            highlights={highlights}
            onChange={setHighlights}
            sharePath={studySharePath}
            cardKind="companion"
          />
        </h1>
        {study.overview && (
          <div className="flex items-start gap-1.5">
            <p className="text-base text-slate-400 leading-relaxed flex-1">
              <HighlightableText
                sessionId={session.id}
                block="overview"
                text={study.overview}
                highlights={highlights}
                onChange={setHighlights}
                renderText={(t) => <InlineStrongs text={t} sessionId={session.id} />}
                sharePath={studySharePath}
                cardKind="companion"
              />
            </p>
            <CopyButton text={buildOverviewText(study)} label="Overview copied" testid="copy-overview" className="shrink-0 -mt-0.5" />
          </div>
        )}
        {fallbackTranslation && (
          <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
            {passage.requested_translation.toUpperCase()} isn&rsquo;t licensed yet — showing{" "}
            {passage.translation_name}.
          </div>
        )}
      </header>

      {/* Audio reader / copy / print — tucked behind a "Tools" toggle so the study
          itself is the first thing you see, not a row of buttons. */}
      <div className="-mt-6">
        <button
          data-testid="toggle-study-tools"
          onClick={() => setToolsOpen((o) => !o)}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#0E131F] hover:bg-[#10222A] text-slate-400 hover:text-[#00E5FF] border border-[#29344D] pl-3 pr-3 py-2 text-sm font-medium transition-colors active:scale-95"
        >
          <SlidersHorizontal className="w-4 h-4" strokeWidth={1.75} /> Tools
          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${toolsOpen ? "rotate-180" : ""}`} strokeWidth={1.75} />
        </button>
        {toolsOpen && (
          <div className="mt-2 animate-in fade-in slide-in-from-top-1 duration-200">
            <div className="flex flex-wrap items-center gap-1.5">
              <ListenButton id="study-full" getText={fullStudyText} label="Listen to this study" testid="listen-study" />
              <button
                data-testid="open-readalong"
                onClick={() => setReadAlong(true)}
                className="inline-flex items-center gap-2 rounded-full bg-[#0E131F] hover:bg-[#10222A] text-[#00B8CC] border border-[#00E5FF]/20 pl-3 pr-4 py-2 text-sm font-medium transition-colors active:scale-95"
              >
                <AlignLeft className="w-4 h-4" strokeWidth={1.75} /> Read along
              </button>
              <StudyActions
                study={study}
                passage={passage}
                notes={session.notes || ""}
                answers={answers}
                chat={session.chat || []}
                includeNotes
                testid="study"
              />
              <AudioSettings />
            </div>
            <p className="mt-1.5 text-xs text-slate-500">Prefer to listen? Play the study aloud, follow along with the words highlighted, and adjust the speed. You can also copy the study (or just the Scripture or your notes) or print it / save as PDF.</p>
          </div>
        )}
      </div>

      <ReadAlongPanel id="study-readalong" text={fullStudyText} open={readAlong} onClose={() => setReadAlong(false)} />

      {/* Reading level (Zone A — free) */}
      {onStudyReload && (
        <div className="-mt-2">
          <ReadingLevelToggle session={session} onReload={onStudyReload} />
        </div>
      )}

      {/* Scripture hero (shared) */}
      <ScriptureHero session={session} highlights={highlights} onHighlightsChange={setHighlights} sharePath={studySharePath} />

      {/* Teaching sections */}
      {(study.sections || []).map((sec, si) => (
        <section key={si} className="space-y-5">
          <div className="flex items-center gap-2">
            {sec.heading && (
              <h2 className="font-sans text-xl font-medium text-slate-200 tracking-tight">
                <HighlightableText
                  sessionId={session.id}
                  block={`section-${si}-heading`}
                  text={sec.heading}
                  highlights={highlights}
                  onChange={setHighlights}
                  sharePath={studySharePath}
                  cardKind="companion"
                />
              </h2>
            )}
            <ListenIconButton
              id={`study-sec-${si}`}
              getText={() => sectionText(sec)}
              testid={`listen-section-${si}`}
              title="Listen to this section"
            />
            <CopyButton
              text={buildSectionText(sec)}
              label="Section copied"
              testid={`copy-section-${si}`}
              title="Copy this section"
            />
            <SharePublicDialog
              basePath={`/study/sessions/${session.id}/sections/${si}/share`}
              campaign="section_share"
              ctaLabel="Read the full study:"
              title="Share this section"
              description="Create a public link to just this section, then share it anywhere. Turn it off any time."
              hook={sec.heading || (sec.commentary || "").slice(0, 140)}
              previewTitle={sec.heading || study.title || "A section from a Bible study"}
              emailSubject="A section from a Bible study on OpenWord AI"
              testid={`share-section-${si}`}
              renderTrigger={(openShare) => (
                <button
                  data-testid={`share-section-${si}-open`}
                  onClick={openShare}
                  title="Share this section"
                  aria-label="Share this section"
                  className="p-2 rounded-lg text-slate-500 hover:text-[#00E5FF] hover:bg-[#10222A] transition-colors"
                >
                  <Share2 className="w-4 h-4" strokeWidth={1.6} />
                </button>
              )}
            />
          </div>
          <div className="text-lg leading-relaxed text-slate-300 space-y-4">
            <Paragraphs
              text={sec.commentary}
              sessionId={session.id}
              blockPrefix={`section-${si}`}
              highlights={highlights}
              onHighlightsChange={setHighlights}
              sharePath={studySharePath}
              cardKind="companion"
            />
          </div>
          {(sec.reflect || []).map((q, qi) => (
            <ReflectField
              key={qi}
              sessionId={session.id}
              sectionIndex={si}
              questionIndex={qi}
              question={q}
              initial={answers[`${si}-${qi}`]}
              highlights={highlights}
              onHighlightsChange={setHighlights}
              sharePath={studySharePath}
            />
          ))}
        </section>
      ))}

      {/* Strong's */}
      {!hideStrongs && (
        <StrongsAccordion
          entries={study.strongs}
          sessionId={session.id}
          reference={study.passage_reference}
        />
      )}

      {/* Related passages (cross-references) */}
      <CrossReferences session={session} />

      {/* Summary */}
      {study.summary && (
        <section className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-6 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-500">
              <ScrollText className="w-4 h-4" strokeWidth={1.5} /> Summary
            </div>
            <CopyButton text={buildSummaryText(study)} label="Summary copied" testid="copy-summary" title="Copy the summary" />
          </div>
          <p className="text-base text-slate-400 leading-relaxed">
            <HighlightableText
              sessionId={session.id}
              block="summary"
              text={study.summary}
              highlights={highlights}
              onChange={setHighlights}
              sharePath={studySharePath}
              cardKind="companion"
            />
          </p>
          <p className="text-sm text-slate-400 leading-relaxed pt-1">
            Want to sit with this longer? {" "}
            <button
              data-testid="summary-go-deeper-cta"
              onClick={openGoDeeper}
              className="font-semibold text-[#00E5FF] hover:text-[#00B8CC] underline decoration-[#00E5FF]/30 hover:decoration-[#00B8CC] transition-colors"
            >
              Go Deeper
            </button>{" "}
            for further understanding.
          </p>
        </section>
      )}

      {/* Exercise */}
      {study.exercise && (
        <section className="rounded-2xl bg-[#10222A] border border-[#00E5FF]/15 p-6 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-[#00E5FF]">
              <HeartHandshake className="w-4 h-4" strokeWidth={1.5} /> An exercise for you
            </div>
            <CopyButton text={buildExerciseText(study)} label="Exercise copied" testid="copy-exercise" title="Copy this exercise" />
          </div>
          <p className="text-base text-slate-300 leading-relaxed">
            <HighlightableText
              sessionId={session.id}
              block="exercise"
              text={study.exercise}
              highlights={highlights}
              onChange={setHighlights}
              sharePath={studySharePath}
              cardKind="companion"
            />
          </p>
        </section>
      )}

      {/* Teach & Share (Pillar 4) — turn this study + your own reflection into a teaching
          resource (sermon outline, teaching notes, devotional, discussion guide, nugget).
          Gated on 3+ Go Deeper exchanges; folds in the old devotional/discussion generators. */}
      {/* Teach & Share — available on mobile AND desktop; shown only when the teach_share flag is on. */}
      {showGenerators && teachEnabled && <TeachAndShare session={session} />}
    </div>
  );
}
